import { canEditWorkspace } from '../components/settings/workspaceUtils';
import { bowerGlyph, materialGlyph, type GlyphRef } from '../theme/glyph';

/**
 * Settings tabs — single source of truth for slug, label, sidebar icon, group
 * placement, AND per-tab visibility.
 *
 * This lives outside `AccountSettings.tsx` so the settings page and the Cmd-K
 * command palette derive the *identical* set of visible tabs from one place —
 * no forked role/PHI logic. Render order here drives both the sidebar list and
 * the Chakra `TabPanels` index, so deep-links (`/settings?tab=foo`),
 * back/forward, and Stripe's `success_url` all line up.
 *
 * Visibility predicates reuse the exact rules the page enforced inline:
 *   - Billing    → `canEditWorkspace` (admin/owner only)
 *   - Connectors → non-PHI workspaces only (gateway provider's BAA unsigned;
 *     the backend also refuses connection writes from PHI workspaces)
 */

export type SettingsTabSlug =
  | 'profile'
  | 'security'
  | 'preferences'
  | 'connectors'
  | 'ai-app-access'
  | 'workspace'
  | 'members'
  | 'billing';

export interface SettingsTabVisibilityContext {
  /**
   * Active workspace doc. Typed `unknown` because visibility predicates read
   * it defensively at runtime, including cached payloads from older builds.
   */
  readonly workspace: unknown;
  /** Whether the active workspace has PHI mode enabled. */
  readonly isPhiWorkspace: boolean;
}

/**
 * A searchable FIELD inside a settings tab (ORCID iD, Theme, Delete account …).
 *
 * Fields are first-class in the palette AND the in-page settings search: each
 * one is its own result that lands the user on the right tab. Co-locating them
 * here (next to the tab) is what makes field search self-maintaining — you add a
 * field to a form and its search entry in the same SSOT, right here.
 *
 * `label` is what the user sees in results, so it must match the visible copy on
 * the page. `keywords` are extra synonyms; the label's own words are always
 * searchable without listing them.
 *
 * This is easy to get wrong: review found two labels here that never appeared on
 * the page — 'Active sessions' (the row reads "Revoke all sessions"; the phrase
 * only occurs in a post-click toast) and 'PHI compliance' (the toggle is
 * deliberately labelled "Restricted mode (HIPAA-ready)"). Put the real rendered
 * copy in `label` and the words users might type in `keywords`.
 *
 * COVERAGE LIMIT — read before trusting this as drift-proof: a *presence guard*
 * (renders the tab and asserts each declared field is really on the page) exists
 * for **Profile only** (`ProfileTab.test.tsx`). Fields on Security, Preferences,
 * Workspace and Connect-external-AI are NOT guarded — rename or remove one and
 * search will keep advertising a field that no longer exists. Adding a field
 * here is safe; renaming/removing one on those tabs needs a manual check until
 * the guard is extended (see that test for the pattern to copy).
 */
export interface SettingsFieldMeta {
  readonly label: string;
  readonly keywords?: readonly string[];
}

export interface SettingsTabMeta {
  readonly slug: SettingsTabSlug;
  readonly label: string;
  readonly glyph: GlyphRef;
  readonly group: 'account' | 'workspace';
  readonly visible: (ctx: SettingsTabVisibilityContext) => boolean;
  /**
   * Search synonyms for Cmd-K, co-located with the tab so adding a tab (or a new
   * section inside one) carries its own findability in ONE place — no separate
   * keyword map to keep in sync. The tab's label and the word "settings" are
   * added automatically by the command registry; list only the extra synonyms.
   */
  readonly keywords: readonly string[];
  /**
   * Searchable fields inside this tab. REQUIRED (use `[]` deliberately) so a new
   * tab forces a conscious "which fields here are worth searching?" decision —
   * same discipline as NAV_EXCLUDED_ROUTES. `[]` means the tab is reached by its
   * own name/keywords only (its content is dynamic or card-shaped, not fields).
   */
  readonly fields: readonly SettingsFieldMeta[];
}

const always = (): boolean => true;

export const SETTINGS_TABS: ReadonlyArray<SettingsTabMeta> = [
  {
    slug: 'profile',
    label: 'Profile',
    glyph: materialGlyph('person'),
    group: 'account',
    visible: always,
    keywords: ['account', 'avatar', 'photo'],
    fields: [
      { label: 'Display name', keywords: ['name'] },
      { label: 'ORCID iD', keywords: ['orcid', 'researcher id', 'research identity'] },
      { label: 'Your role', keywords: ['role', 'position'] },
    ],
  },
  {
    slug: 'security',
    label: 'Security',
    glyph: materialGlyph('security'),
    group: 'account',
    visible: always,
    keywords: ['2fa', 'login', 'auth', 'mfa'],
    fields: [
      { label: 'Password', keywords: ['change password'] },
      { label: 'Sign-in methods', keywords: ['google', 'sso', 'linked accounts'] },
      // Label must match the rendered row ("Revoke all sessions"); "active
      // sessions" only ever appears in a post-click toast, so it's a keyword.
      {
        label: 'Revoke all sessions',
        keywords: ['active sessions', 'sign out everywhere', 'sessions', 'account access'],
      },
      { label: 'Analytics cookies', keywords: ['privacy', 'tracking', 'consent'] },
      { label: 'Export my data', keywords: ['download data', 'gdpr'] },
      { label: 'Delete account', keywords: ['close account', 'danger zone'] },
    ],
  },
  {
    slug: 'preferences',
    label: 'Preferences',
    glyph: materialGlyph('tune'),
    group: 'account',
    visible: always,
    keywords: ['notifications'],
    fields: [
      { label: 'Theme', keywords: ['appearance', 'dark mode', 'light mode'] },
      {
        label: 'What Bower remembers',
        keywords: ['memory', 'memories', 'personalization', 'remembers', 'bird'],
      },
    ],
  },
  {
    slug: 'connectors',
    label: 'Connectors',
    glyph: materialGlyph('electrical_services'),
    group: 'account',
    visible: (ctx) => !ctx.isPhiWorkspace,
    keywords: ['integrations', 'oauth', 'connect', 'apps'],
    // Rows are the connected-tool catalogue, fetched from the API at runtime —
    // no static fields to enumerate. Reached by the tab name / keywords.
    fields: [],
  },
  {
    slug: 'ai-app-access',
    label: 'Connect external AI',
    glyph: materialGlyph('hub'),
    group: 'account',
    visible: always,
    keywords: ['ai', 'external', 'api access'],
    fields: [{ label: 'MCP server URL', keywords: ['mcp', 'endpoint', 'api url'] }],
  },
  // Workspace uses the custom Bower "workspace" glyph (matches mobile bottom nav)
  {
    slug: 'workspace',
    label: 'Workspace',
    glyph: bowerGlyph('workspace'),
    group: 'workspace',
    visible: always,
    keywords: ['workspace memory'],
    fields: [
      { label: 'Workspace name', keywords: ['rename workspace'] },
      { label: 'Institution', keywords: ['organisation', 'organization', 'university'] },
      {
        label: 'Research context',
        keywords: ['what your lab works on', 'field of research'],
      },
      { label: 'Team size', keywords: ['lab size'] },
      // PhiComplianceToggle deliberately labels this generically ("Restricted
      // mode") so it covers PHI *and* other highly sensitive data — don't
      // reintroduce "PHI" as user-facing copy; keep it as a search synonym.
      {
        label: 'Restricted mode (HIPAA-ready)',
        keywords: ['hipaa', 'phi', 'compliance', 'restricted'],
      },
    ],
  },
  {
    slug: 'members',
    label: 'Members',
    glyph: materialGlyph('group'),
    group: 'workspace',
    visible: always,
    keywords: ['team', 'invite', 'people', 'roles'],
    // The member + pending-invitation lists are dynamic data, not static fields.
    fields: [],
  },
  {
    slug: 'billing',
    label: 'Billing',
    glyph: materialGlyph('credit_card'),
    group: 'workspace',
    visible: (ctx) => canEditWorkspace(ctx.workspace),
    keywords: ['plan', 'subscription', 'invoice', 'payment', 'upgrade'],
    // Plan / usage / quota cards are driven by the billing API, not static fields.
    fields: [],
  },
];

/** Tabs visible for the given context, preserving declaration (render) order. */
export function visibleSettingsTabs(
  ctx: SettingsTabVisibilityContext,
): ReadonlyArray<SettingsTabMeta> {
  return SETTINGS_TABS.filter((tab) => tab.visible(ctx));
}
